import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';
import { wallboardApi } from '../../../api/client';
import TenantPicker, { useTenantFilter } from './TenantPicker';
import HelpButton from '../../help/HelpButton';

const REFRESH_MS = 5_000;

// Pie slice colors per agent state (hex so recharts <Cell> renders them).
const STATE_PIE_COLOR = {
  ready:            '#22c55e',
  aux:              '#f59e0b',
  wrap:             '#3b82f6',
  on_call:          '#a855f7',
  on_call_outbound: '#14b8a6',
  on_hold:          '#fb923c',
};
// Order the slices consistently.
const PIE_ORDER = ['ready', 'on_call', 'on_call_outbound', 'on_hold', 'wrap', 'aux'];

// A member is "logged in" (should appear on this dashboard) when their ACD
// state is NOT `logout` (= Not Ready = technically logged out). Per Fernando
// (2026-08-11) hide those.
//
// IMPORTANT: do NOT key off drift='portal_only'. An agent who is genuinely
// logged in but currently ON A CALL is auto-paused/removed from the AMI queue
// membership snapshot, so they legitimately come back as portal_only while
// active (verified live 2026-08-11: all 12 ready/on-call Bat-Spanish agents
// were portal_only). Filtering on portal_only would hide the very agents we
// want to show. The agent_live_state `state` is the authoritative logged-in
// signal here.
function isLoggedIn(m) {
  return m && m.state !== 'logout';
}

const STATE_COLOR = {
  ready:            'bg-accent-green/20 text-green-700 dark:text-green-300 border-accent-green/40',
  aux:              'bg-accent-amber/20 text-amber-700 dark:text-amber-300 border-accent-amber/40',
  wrap:             'bg-accent-blue/20 text-blue-700 dark:text-blue-300 border-accent-blue/40',
  on_call:          'bg-accent-purple/20 text-purple-700 dark:text-purple-300 border-accent-purple/40',
  // Agent-state hardening (2026-07-09): distinct chip for outbound calls.
  on_call_outbound: 'bg-teal-100 text-teal-700 dark:text-teal-300 border-teal-400/40',
  // ACD phase 1 (2026-07-09): agent whose caller is on MOH.
  on_hold:          'bg-accent-orange/20 text-orange-700 dark:text-orange-300 border-accent-orange/40',
  logout:           'bg-surface-subtle text-surface-muted border-surface-border',
};
const STATE_LABEL = {
  ready: 'Ready', aux: 'AUX', wrap: 'Wrap',
  on_call: 'On Call ↙',
  on_call_outbound: 'On Outbound ↗',
  on_hold: 'On Hold',
  logout: 'Not Ready',
};

function fmt(s) {
  if (!s || s < 0) return '0s';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); return `${m}m ${s % 60}s`;
}

export default function QueuesView() {
  const [clientId, setClientId] = useTenantFilter();
  const { data: queues = [], isLoading, error } = useQuery({
    queryKey: ['wallboard', 'queues', clientId || ''],
    queryFn: () => wallboardApi.queues(clientId || undefined),
    refetchInterval: REFRESH_MS,
  });
  const [filter, setFilter] = useState({ queue: '', agent: '', skill: '', did: '' });

  // Filter options drawn from the live snapshot.
  const { agentOpts, skillOpts } = useMemo(() => {
    const agents = new Map();
    const skills = new Set();
    for (const q of queues) {
      // members is a COUNT (int) on the backend; the per-member array lives at
      // members_detail. Iterating q.members threw a TypeError and crashed the page.
      for (const m of (q.members_detail || [])) {
        if (m.extension) agents.set(m.extension, m.name || m.extension);
        if (m.skill_level != null) skills.add(m.skill_level);
      }
    }
    return {
      agentOpts: [...agents.entries()].map(([ext, name]) => ({ ext, name }))
        .sort((a, b) => a.ext.localeCompare(b.ext)),
      skillOpts: [...skills].sort((a, b) => a - b),
    };
  }, [queues]);

  const filteredQueues = useMemo(() => {
    return queues
      .filter((q) => !filter.queue || q.queue_id === filter.queue)
      .filter((q) => !filter.did || (q.did || '').includes(filter.did))
      .map((q) => {
        // Always drop logged-out / not-currently-logged-in members from the
        // ACD > Queues display (they're technically logged out).
        let members_detail = (q.members_detail || []).filter(isLoggedIn);
        if (filter.agent || filter.skill) {
          members_detail = members_detail.filter((m) => {
            if (filter.agent && m.extension !== filter.agent) return false;
            if (filter.skill && String(m.skill_level) !== String(filter.skill)) return false;
            return true;
          });
        }
        // Per-queue agent-state breakdown for the pie (logged-in only).
        const stateCounts = {};
        for (const m of members_detail) {
          stateCounts[m.state] = (stateCounts[m.state] || 0) + 1;
        }
        const pieData = PIE_ORDER
          .filter((s) => stateCounts[s])
          .map((s) => ({
            key: s,
            name: STATE_LABEL[s] || s,
            value: stateCounts[s],
            fill: STATE_PIE_COLOR[s] || '#94a3b8',
          }));
        const loggedIn = members_detail.length;
        return { ...q, members_detail, pieData, loggedIn };
      });
  }, [queues, filter]);

  if (error) return <div className="p-6 text-sm text-accent-red">Failed to load: {error.message}</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold">Queues — detail</h2>
          <div className="ml-4 inline-block align-middle">
            <TenantPicker value={clientId} onChange={setClientId} />
          </div>
          <p className="text-xs text-surface-muted">Refreshes every {REFRESH_MS / 1000}s. Showing portal-managed queues only.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <HelpButton moduleKey="acd_queues" />
          <select
            value={filter.queue}
            onChange={(e) => setFilter((f) => ({ ...f, queue: e.target.value }))}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
          >
            <option value="">All queues</option>
            {queues.map((q) => (
              <option key={q.queue_id} value={q.queue_id}>{q.queue_id} · {q.name}</option>
            ))}
          </select>
          <select
            value={filter.agent}
            onChange={(e) => setFilter((f) => ({ ...f, agent: e.target.value }))}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
          >
            <option value="">All agents</option>
            {agentOpts.map((a) => (
              <option key={a.ext} value={a.ext}>{a.ext} · {a.name}</option>
            ))}
          </select>
          <select
            value={filter.skill}
            onChange={(e) => setFilter((f) => ({ ...f, skill: e.target.value }))}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
          >
            <option value="">All skills</option>
            {skillOpts.map((n) => (
              <option key={n} value={n}>Skill {n}</option>
            ))}
          </select>
          <input
            type="search"
            placeholder="DID / DN"
            value={filter.did}
            onChange={(e) => setFilter((f) => ({ ...f, did: e.target.value }))}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin w-28"
          />
          {(filter.queue || filter.agent || filter.skill || filter.did) && (
            <button
              onClick={() => setFilter({ queue: '', agent: '', skill: '', did: '' })}
              className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {isLoading && !queues.length && <div className="text-sm text-surface-muted">Loading…</div>}
      {!isLoading && queues.length === 0 && (
        <div className="text-sm text-surface-muted">No portal-managed queues found. Create one in <strong>Admin › Queues</strong>.</div>
      )}

      <div className="space-y-4">
        {filteredQueues.map((q) => (
          <div key={q.queue_id} className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel shadow-thin">
            <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between flex-wrap gap-2">
              <div>
                <span className="font-mono text-xs mr-2 text-surface-muted">{q.queue_id}</span>
                <span className="font-semibold">{q.name}</span>
                {q.auto_answer && <span className="ml-2 text-xs text-accent-amber" title="Auto-answer enabled">✨ auto-answer</span>}
                {q.whisper_enabled && <span className="ml-2 text-xs text-accent-blue" title="Whisper enabled">🔊 whisper</span>}
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                <div>
                  <span className="text-surface-muted mr-1">In Queue:</span>
                  <span className={`font-semibold ${q.callers > 0 ? 'text-accent-red' : ''}`}>{q.callers}</span>
                </div>
                <div>
                  <span className="text-surface-muted mr-1">On Hold:</span>
                  <span className={`font-semibold ${q.calls_on_hold > 0 ? 'text-accent-orange' : ''}`}>{q.calls_on_hold ?? 0}</span>
                </div>
                <div>
                  <span className="text-surface-muted mr-1">Handled today:</span>
                  <span className="font-semibold">{q.calls_handled_today ?? 0}</span>
                </div>
                <div>
                  <span className="text-surface-muted mr-1">Abandoned today:</span>
                  <span className={`font-semibold ${(q.calls_abandoned_today ?? 0) > 0 ? 'text-accent-amber' : ''}`}>{q.calls_abandoned_today ?? 0}</span>
                </div>
                <div>
                  <span className="text-surface-muted mr-1">Longest wait:</span>
                  <span className={`font-semibold ${q.longest_wait > 30 ? 'text-accent-red' : ''}`}>{fmt(q.longest_wait)}</span>
                </div>
                <div>
                  <span className="text-surface-muted mr-1">SLA:</span>
                  <span className="font-semibold">{q.sla > 0 ? `${(q.sla * 100).toFixed(0)}%` : '—'}</span>
                </div>
                <div>
                  <span className="text-surface-muted mr-1">Members:</span>
                  <span className="font-semibold">{q.members_available}/{q.members}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 p-4">
              {/* Waiting callers */}
              <div>
                <h3 className="text-xs uppercase tracking-wider text-surface-muted mb-2">Callers waiting ({q.waiting?.length || 0})</h3>
                {(!q.waiting || q.waiting.length === 0) ? (
                  <div className="text-xs text-surface-muted py-3">No callers in queue.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs text-surface-muted text-left">
                      <tr>
                        <th className="px-2 py-1">#</th>
                        <th className="px-2 py-1">Caller</th>
                        <th className="px-2 py-1 text-right">Wait</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.waiting.map((c) => (
                        <tr key={c.position} className="border-t border-surface-border dark:border-surface-dark-border">
                          <td className="px-2 py-1 font-mono">{c.position}</td>
                          <td className="px-2 py-1">{c.caller_name || c.caller_num || '—'} <span className="text-xs text-surface-muted">{c.caller_num}</span></td>
                          <td className={`px-2 py-1 text-right font-mono ${c.wait_secs > 30 ? 'text-accent-red font-semibold' : ''}`}>{fmt(c.wait_secs)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="mt-3 text-xs text-surface-muted grid grid-cols-2 gap-2">
                  <div>Handled today: <strong>{q.calls_handled_today ?? 0}</strong></div>
                  <div>Abandoned today: <strong>{q.calls_abandoned_today ?? 0}</strong></div>
                  <div>Avg hold time: <strong>{fmt(q.avg_hold_time)}</strong> <span className="opacity-60">(cum.)</span></div>
                  <div>Avg talk time: <strong>{fmt(q.avg_talk_time)}</strong> <span className="opacity-60">(cum.)</span></div>
                </div>

                {/* Agent-state breakdown (logged-in agents only) — 2026-08-11 */}
                <div className="mt-4 pt-3 border-t border-surface-border dark:border-surface-dark-border">
                  <h3 className="text-xs uppercase tracking-wider text-surface-muted mb-1">
                    Agents logged in ({q.loggedIn ?? 0})
                  </h3>
                  {(!q.pieData || q.pieData.length === 0) ? (
                    <div className="text-xs text-surface-muted py-3">No agents logged in.</div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div style={{ width: 150, height: 150 }} className="shrink-0 text-surface-body dark:text-surface-dark-body">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={q.pieData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={34}
                              outerRadius={62}
                              paddingAngle={1}
                              stroke="none"
                            >
                              {q.pieData.map((d) => <Cell key={d.key} fill={d.fill} />)}
                            </Pie>
                            <Tooltip
                              formatter={(val, name) => [`${val} agent${val === 1 ? '' : 's'}`, name]}
                              contentStyle={{ fontSize: 12 }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <ul className="text-xs space-y-1 min-w-0">
                        {q.pieData.map((d) => (
                          <li key={d.key} className="flex items-center gap-2">
                            <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.fill }} />
                            <span className="truncate">{d.name}</span>
                            <span className="font-semibold ml-auto">{d.value}</span>
                          </li>
                        ))}
                        <li className="flex items-center gap-2 pt-1 mt-1 border-t border-surface-border/60 dark:border-surface-dark-border/60">
                          <span className="text-surface-muted">Total logged in</span>
                          <span className="font-semibold ml-auto">{q.loggedIn ?? 0}</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Members */}
              <div>
                <h3 className="text-xs uppercase tracking-wider text-surface-muted mb-2">Members ({q.members_detail?.length || 0})</h3>
                {(!q.members_detail || q.members_detail.length === 0) ? (
                  <div className="text-xs text-surface-muted py-3">No members logged in.</div>
                ) : (
                  <ul className="space-y-1">
                    {q.members_detail.map((m, i) => (
                      <li key={`${m.extension}-${i}`} className="flex items-center justify-between text-sm border-b border-surface-border/50 dark:border-surface-dark-border/50 py-1">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-xs">{m.extension || '—'}</span>
                          <span className="truncate">{m.name || '—'}</span>
                          {/* 2026-07-16 drift markers.
                              ami_only    = Asterisk has this member, portal doesn't
                                            (Asterisk was edited outside portal, or
                                             pending resync). Shown in amber.
                              portal_only = portal has this member, Asterisk doesn't
                                            (agent logged out, or sync failed).
                                            Shown in gray-italic (ghost row). */}
                          {m.drift === 'ami_only' && (
                            <span
                              title="Drift: this member exists on Asterisk but NOT in the CX portal's queue config. Fix in Admin › Queues."
                              className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/50 text-amber-700 dark:text-amber-300 bg-amber-100/40 dark:bg-amber-900/20"
                            >⚠ Asterisk-only</span>
                          )}
                          {m.drift === 'portal_only' && (
                            <span
                              title="Configured in portal but not currently logged in to Asterisk. Shown for completeness."
                              className="text-[10px] px-1.5 py-0.5 rounded border border-surface-border/50 text-surface-muted italic"
                            >portal-only</span>
                          )}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border shrink-0 ${STATE_COLOR[m.state] || STATE_COLOR.logout}`}>
                          {STATE_LABEL[m.state] || m.state}
                          {m.state === 'aux' && m.paused_reason && <span className="ml-1 opacity-75">· {m.paused_reason}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
